import { numericIDPattern, shortCodePattern } from './route-path';

export interface DesignAssetFile {
  file_id: string;
  file_name: string;
}

export interface DesignAssetPreview {
  group_id: string;
  asset_id: string;
  group_short_code?: string | null;
  asset_short_code?: string | null;
  asset_name: string;
  description: string;
  can_download?: boolean;
  images?: Array<{
    file_id: string;
    url: string;
    alt?: string;
    file_description?: string;
    file_link_url?: string;
    display_size?: 'sm' | 'md' | 'lg' | 'full';
  }>;
  video?: {
    file_id: string;
    url: string;
    file_name: string;
    file_description?: string;
    file_link_url?: string;
  };
  downloads?: DesignAssetFile[];
}

export interface DesignAssetGroupPreview {
  group_id: string;
  group_short_code?: string | null;
  group_name: string;
  group_tag: string;
  group_description: string;
  assets: DesignAssetPreview[];
}

export interface DesignAssetHomeGroupPreview {
  group_id: string;
  group_short_code?: string | null;
  group_name: string;
  group_tag: string;
  group_description: string;
  first_asset_id?: string | null;
  first_asset_short_code?: string | null;
}

interface DesignAssetHomeResponse {
  title?: string | null;
  description?: string | null;
  background_url?: string | null;
  background_type?: number | string | null;
  background_dimmed?: boolean | null;
  groups?: DesignAssetHomeGroupPreview[] | null;
}

interface DesignAssetAPIAsset extends DesignAssetPreview {
  videos?: DesignAssetPreview['video'][];
}

interface DesignAssetAPIGroup extends Omit<DesignAssetGroupPreview, 'assets'> {
  assets?: DesignAssetAPIAsset[];
}

interface DesignAssetDetailResponse {
  asset: DesignAssetPreview;
  groups?: DesignAssetAPIGroup[];
}

// 与 S 大会一致使用官网同源 Portal 前缀，由网关或本地 Whistle 转发到 Portal。
const portalPrefix = '/portalWebAPI/designAsset';

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${portalPrefix}${path}`, {
    cache: 'no-store',
    ...options,
  });
  if (!response.ok) throw new Error('品牌资源服务暂不可用');
  const result = await response.json();
  if (result?.code !== 0)
    throw new Error(result?.msg || '品牌资源服务暂不可用');
  return result.data as T;
}

export async function getDesignAssetHome(): Promise<DesignAssetHomeResponse> {
  return request<DesignAssetHomeResponse>('/home', { method: 'POST' });
}

export async function getDesignAssetDetail(
  groupToken: string,
  assetToken: string
): Promise<{
  asset: DesignAssetPreview;
  groups: DesignAssetGroupPreview[];
}> {
  const searchParams = new URLSearchParams();
  if (
    numericIDPattern.test(groupToken) &&
    numericIDPattern.test(assetToken)
  ) {
    searchParams.set('group_id', groupToken);
    searchParams.set('asset_id', assetToken);
  } else if (
    shortCodePattern.test(groupToken) &&
    shortCodePattern.test(assetToken)
  ) {
    searchParams.set('group_short_code', groupToken.toLowerCase());
    searchParams.set('asset_short_code', assetToken.toLowerCase());
  } else {
    // 路由身份只能成对使用数字 ID 或短码，禁止混合参数穿透到 Portal。
    throw new TypeError('Design asset route tokens must use one valid mode');
  }

  const data = await request<DesignAssetDetailResponse>(
    `/detail?${searchParams.toString()}`
  );
  return {
    ...data,
    groups: (data.groups || []).map((group) => ({
      ...group,
      assets: (group.assets || []).map((asset) => ({
        ...asset,
        video: asset.videos?.[0],
      })),
    })),
  };
}

// 下载前复用详情接口实时校验权限，不更新页面中的资源列表状态。
export async function getDesignAssetDownloadPermission(
  groupID: string,
  assetID: string,
  fileID: string
): Promise<{ canDownload: boolean; file?: DesignAssetFile }> {
  if (!numericIDPattern.test(groupID) || !numericIDPattern.test(assetID)) {
    throw new TypeError('Design asset download IDs must both be numeric');
  }
  const searchParams = new URLSearchParams({
    group_id: groupID,
    asset_id: assetID,
  });
  const data = await request<DesignAssetDetailResponse>(
    `/detail?${searchParams.toString()}`
  );
  const file = (data.asset?.downloads || []).find(
    (item: DesignAssetFile) => item.file_id === fileID
  );
  return {
    canDownload: Boolean(data.asset?.can_download),
    file,
  };
}

export const getDesignAssetDownloadURL = (fileID: string, fileName: string) =>
  `${portalPrefix}/download?file_id=${encodeURIComponent(
    fileID
  )}&filename=${encodeURIComponent(fileName)}`;
